import React, { useEffect, useState } from 'react'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import defaultTexts from '../../texts/texts'
import constants from '../../config/constants'
import { request } from '../../_network/request'
import { clientAccountsURLs } from '../../_network/apiUrls'
import CustomTextField from '../_common/_form_fields/CustomTextField'
import CustomSelectField from '../_common/_form_fields/CustomSelectField'
import CustomBackdrop from '../_common/CustomBackdrop'

const UserEdit = (props) => {
  const { handleEditUserDialogClose, editUser, setFetchUsers } = props
  const theme = useTheme()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [userRole, setUserRole] = useState('')
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const roleList = [
    {
      keyword: 'Admin',
      value: 'admin',
    },
    {
      keyword: 'User',
      value: 'user',
    },
  ]

  const formControlStyle = {
    background: theme.palette.aliceBlue,
    color: theme.palette.vodka,
    borderRadius: '5px',
    width: '100%',
  }

  const outlinedInputStyle = {
    '& .MuiInputBase-input::label': {
      color: theme.palette.vodka,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
  }

  const errorHelperStyle = {
    color: theme.palette.coralRed,
    paddingLeft: '5px',
  }

  const fieldBoxStyle = {
    padding: '12px 0px'
  }

  const saveButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    marginTop: '20px',
    textTransform: 'capitalize',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const handleChangeFirstName = (e) => {
    const value = e.target.value
    setFirstName(value)
    if (value) {
      setErrors(prev => {return { ...prev, firstName: null }})
    }    
  }

  const handleChangeLastName = (e) => {
    const value = e.target.value
    setLastName(value)
    if (value) {
      setErrors(prev => {return { ...prev, lastName: null }})
    }    
  }

  const handleChangeUserRole = (value) => {
    setUserRole(value)
    if (value) {
      setErrors(prev => {return { ...prev, userRole: null }})
    }    
  }

  const scrollToTop = () => {
    const element = document.getElementById('formStart')
    element.scrollIntoView({ behavior: 'smooth' })
  }

  const editUserRequest = async (postData) => {
    const url = constants.core_service_domain + '/' + clientAccountsURLs.getUsers + '/' + editUser.id
      
    const request_data = { url, method: 'PUT', data: postData }
    
    const response = await request(request_data)
    
    if (response && response.status_code && response.status_code < 400) {
      handleEditUserDialogClose()
      setFetchUsers(prev => prev + 1)
    } else {
      scrollToTop()
      setErrors(prev => {
        return {
          ...prev,
          generalError: defaultTexts.anErrorOccured,
        }
      })
    }
  }

  const handleClickSubmit = async () => {
    setIsLoading(true)
    let _errors = {}

    if (!firstName) {
      _errors.firstName = defaultTexts.pleaseFillOut
    }

    if (!lastName) {
      _errors.lastName = defaultTexts.pleaseFillOut
    }

    if (!userRole) {
      _errors.userRole = defaultTexts.pleaseFillOut
    }

    if (Object.keys(_errors).length === 0) {
      let postData = {
        firstName,
        lastName,
      }

      if (userRole !== 'owner') {
        postData.role = userRole
      }

      editUserRequest(postData)
    }
    
    if (Object.keys(_errors).length > 0) {
      scrollToTop()
      _errors.generalError = defaultTexts.anErrorOccured
      setErrors(_errors)
    }

    setIsLoading(false)
  }

  useEffect(() => {
    if (editUser) {
      setFirstName(editUser.firstName)
      setLastName(editUser.lastName)
      setUserRole((editUser.roles && editUser.roles.length > 0) ? editUser.roles[0] : '')
    } else {
      setFirstName('')
      setLastName('')
      setUserRole('')
    }
  }, [editUser])
  
  return (
    <Box id='formStart'>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      {
        errors.generalError &&
        <Alert severity='error' sx={{ fontSize: '16px' }}>{errors.generalError}</Alert>
      }

      <Box sx={fieldBoxStyle}>
        <CustomTextField 
          variant='outlined'
          label={defaultTexts.firstName}
          currentValue={firstName}
          setCurrentValue={handleChangeFirstName}
          required={true}
          error={errors.firstName ? true : false}
          loginHelperText={errors.firstName}
          loginHelperSx={errorHelperStyle}
          size='small'
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={fieldBoxStyle}>
        <CustomTextField 
          variant='outlined'
          label={defaultTexts.lastName}
          currentValue={lastName}
          setCurrentValue={handleChangeLastName}
          required={true}
          error={errors.lastName ? true : false}
          loginHelperText={errors.lastName}
          loginHelperSx={errorHelperStyle}
          size='small'
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      {
        userRole !== 'owner' &&
        <Box sx={fieldBoxStyle}>
          <CustomSelectField
            label={defaultTexts.userRole}
            items={roleList} 
            currentValue={userRole}
            setCurrentValue={handleChangeUserRole}
            required={true}
            error={errors.userRole ? true : false}
            loginHelperText={errors.userRole}
            loginHelperSx={errorHelperStyle}
            self_item_titles={true}
            size='small'
            sx={formControlStyle}
            outlinedInputSx={outlinedInputStyle}
          />
        </Box>
      }      

      <Stack
        direction='row'
        justifyContent='flex-end'
      >
        <Button
          variant='contained'
          disableRipple
          disableElevation
          disabled={false}
          onClick={handleClickSubmit}
          sx={saveButtonStyle}
        >
          {defaultTexts.save}
        </Button>
      </Stack>
    </Box>
  )
}

export default UserEdit